import { Separator } from "@design-system/ui/separator";
import { createPortal } from "@design-system/utils/portals";
import { trans, plainTrans } from "@mongez/localization";
import { Link, useLocation } from "@remix-run/react";
import { Facebook } from "lucide-react";
import { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { CopyIcon, XIcon } from "../Icons";
import { Portal } from "../Portals";
import { successToast } from "../toast";

export const sharePortal = createPortal("share-portal");

export function SharePortal() {
  const { href } = sharePortal.useData();

  const location = useLocation();
  const [url, setUrl] = useState(href);

  useEffect(() => {
    if (href) {
      setUrl(href);
      return;
    }

    const url = new URL(
      location.pathname,
      window && window.location ? window.location.origin : "",
    );
    setUrl(url.toString());
  }, [location.pathname, href]);

  return (
    <Portal portal={sharePortal} width="md">
      <div className="flex max-w-full flex-col items-center gap-6 rounded-2xl bg-white p-6 text-center shadow-lg dark:bg-gray-800 md:p-8">
        <div className="flex w-full flex-wrap items-center justify-center gap-4">
          <Link
            target="_blank"
            to={`https://api.whatsapp.com/send?text=${url}`}
            rel="noreferrer"
            onClick={sharePortal.close}
            className="flex items-center gap-3 rounded-full bg-[#25D366] px-6 py-3 text-white transition-colors hover:bg-opacity-90"
          >
            <FaWhatsapp className="h-5 w-5" />
            <span className="font-medium">{trans("whatsapp")}</span>
          </Link>
          <Link
            target="_blank"
            to={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
            rel="noreferrer"
            onClick={sharePortal.close}
            className="flex items-center gap-3 rounded-full bg-[#1877F2] px-6 py-3 text-white transition-colors hover:bg-opacity-90"
          >
            <Facebook className="h-5 w-5" />
            <span className="font-medium">{trans("facebook")}</span>
          </Link>
          <Link
            target="_blank"
            to={`https://twitter.com/intent/tweet?text=${url}`}
            rel="noreferrer"
            onClick={sharePortal.close}
            className="flex items-center gap-3 rounded-full bg-black px-6 py-3 text-white transition-colors hover:bg-opacity-90"
          >
            <XIcon className="h-5 w-5" />
            <span className="font-medium">{trans("x")}</span>
          </Link>
        </div>
        <Separator className="my-4 h-[1px] w-full bg-gray-200 dark:bg-gray-700" />
        <div className="relative w-full rounded-xl border border-gray-200 bg-gray-50 px-6 py-4 text-start dark:border-gray-700 dark:bg-gray-900">
          <p className="truncate pr-12 text-gray-700 dark:text-gray-200">
            {url}
          </p>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-2 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
            title={plainTrans("copy")}
            aria-label={plainTrans("copyButton")}
            onClick={() => {
              navigator.clipboard?.writeText(url);
              successToast(trans("linkHasBeenCopied"));
              sharePortal.close();
            }}
          >
            <CopyIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>
    </Portal>
  );
}
